import React from 'react';
import { Form, FormGroup, FormControl, Button, Row, Col, Alert, Table, Modal } from 'react-bootstrap';
import DocConService from '../../_services/doc-con.service';
import config from '../../../config'; // Needed to build the full PDF URL
import { DOC_TYPE_LABELS, getDocTypeLabel } from '../../utils/docTypeConstants';

class DocCon extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            partNumber: '',
            selectedDocType: typeof props.docType === 'number' ? props.docType : 1,
            documents: [],
            message: '',
            error: '',
            showPreview: false,
            previewUrl: null,
            previewFileName: '',
            previewSelectedUrl: null
        };

        this.fileInputRef = React.createRef();
        this.MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
    }

    componentDidMount() {
        this.fetchDocuments();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.docType !== this.props.docType && typeof this.props.docType === 'number') {
            this.setState({
                selectedDocType: this.props.docType,
                partNumber: '',
                selectedFile: null,
                message: '',
                error: '',
                previewSelectedUrl: null,
                documents: []
            }, () => {
                if (this.fileInputRef.current) {
                    try { this.fileInputRef.current.value = ''; } catch (e) { }
                }
                this.fetchDocuments();
            });
        } else if (prevState.partNumber !== this.state.partNumber) {
            this.fetchDocuments();
        }
    }

    componentWillUnmount() {
        const { documents, previewSelectedUrl } = this.state;
        (documents || []).forEach(f => {
            if (f && f.url && f.url.startsWith('blob:')) {
                try { URL.revokeObjectURL(f.url); } catch (e) { }
            }
        });
        if (previewSelectedUrl) {
            try { URL.revokeObjectURL(previewSelectedUrl); } catch (e) { }
        }
    }

    // Return a promise so callers can wait and set messages after refresh
    fetchDocuments = () => {
        const { partNumber, selectedDocType } = this.state;
        const userData = localStorage.getItem('user');

        if (!userData) return Promise.resolve();
        const user = JSON.parse(userData);

        const hasPartNumber = partNumber && partNumber.trim().length > 0;
        const pn = hasPartNumber ? partNumber : '';  // service already handles empty via the URL

        return DocConService.getDocuments(user.accountId, pn, selectedDocType)
            .then(response => {
                const docs = Array.isArray(response.data) ? response.data : [];
                const filteredDocs = docs
                    .filter(doc => doc.documentType === selectedDocType)
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                this.setState({ documents: filteredDocs, error: '' });
            })
            .catch(err => {
                console.warn("Fetch documents in-progress or failed:", err && err.message ? err.message : err);
                this.setState({ documents: [] });
            });
    };

    handleInputChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value, message: '', error: '' });
    };

    onFileChange = (event) => {
        const file = event.target.files && event.target.files[0];
        this.setState({ message: '', error: '' });

        if (this.state.previewSelectedUrl) {
            try { URL.revokeObjectURL(this.state.previewSelectedUrl); } catch (e) { }
        }

        if (!file) {
            this.setState({ selectedFile: null, previewSelectedUrl: null, previewFileName: '' });
            return;
        }

        const fileName = file.name || '';
        const fileSize = file.size || 0;
        const fileType = file.type || '';

        const isPdfByMime = fileType === 'application/pdf';
        const isPdfByExt = fileName.toLowerCase().endsWith('.pdf');

        if (!isPdfByMime && !isPdfByExt) {
            this.setState({
                selectedFile: null,
                previewSelectedUrl: null,
                previewFileName: '',
                error: 'Only PDF files are allowed.'
            });
            if (this.fileInputRef.current) try { this.fileInputRef.current.value = ''; } catch (e) { }
            return;
        }

        if (fileSize > this.MAX_FILE_SIZE) {
            this.setState({
                selectedFile: null,
                previewSelectedUrl: null,
                previewFileName: '',
                error: 'File is too large. Maximum allowed size is 5 MB.'
            });
            if (this.fileInputRef.current) try { this.fileInputRef.current.value = ''; } catch (e) { }
            return;
        }

        const tmpUrl = URL.createObjectURL(file);

        this.setState({
            selectedFile: file,
            previewSelectedUrl: tmpUrl,
            previewFileName: fileName,
            message: '',
            error: ''
        });
    };

    previewSelected = () => {
        const { previewSelectedUrl, previewFileName, selectedFile } = this.state;
        if (!selectedFile || !previewSelectedUrl) {
            this.setState({ error: 'No file selected to preview.' });
            return;
        }
        this.setState({ previewUrl: previewSelectedUrl, showPreview: true, previewFileName });
    };

    handleUpload = () => {
        const { partNumber, selectedFile, selectedDocType, documents } = this.state;
        const user = JSON.parse(localStorage.getItem('user') || '{}');

        if (!partNumber) {
            this.setState({ error: 'Please enter a Part Number first.', message: '' });
            return;
        }
        if (!selectedFile) {
            this.setState({ error: 'Please select a PDF file.', message: '' });
            return;
        }

        // Enforce max 5 files per category
        if (documents && documents.length >= 5) {
            this.setState({ error: 'Maximum limit of 5 files reached. Please delete an existing file before adding a new one.', message: '' });
            return;
        }

        DocConService.upload(selectedFile, selectedDocType, partNumber, user.accountId)
            .then(response => {
                // Refresh list first, then show message so it's not immediately cleared
                this.fetchDocuments().then(() => {
                    this.setState({
                        message: 'File saved to database successfully!',
                        error: '',
                        selectedFile: null,
                        previewSelectedUrl: null,
                        showPreview: false
                    }, () => {
                        if (this.fileInputRef.current) try { this.fileInputRef.current.value = ''; } catch (e) { }
                        // optional: auto-clear message after 5s
                        setTimeout(() => this.setState({ message: '' }), 5000);
                    });
                });
            })
            .catch(err => {
                console.error("Upload error detail:", err);
                let errMsg = 'Database upload failed (Server Error 500).';
                if (err.response && err.response.data && err.response.data.message) {
                    errMsg = err.response.data.message;
                } else if (err.message) {
                    errMsg = err.message;
                }
                this.setState({
                    error: `Upload failed: ${errMsg}. Please ensure the 'documents' folder exists on the server and Part Number is valid.`
                });
            });
    };

    handleDelete = (fileId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;

        if (!DocConService.deleteDocument) {
            this.setState({ error: 'Delete not implemented on backend.' });
            return;
        }

        DocConService.deleteDocument(fileId)
            .then(() => {
                this.fetchDocuments().then(() => {
                    this.setState({
                        message: 'Document deleted successfully!',
                        error: '',
                        showPreview: false
                    }, () => {
                        setTimeout(() => this.setState({ message: '' }), 5000);
                    });
                });
            })
            .catch(err => {
                console.error("Delete error detail:", err);
                let errMsg = 'Deletion failed.';
                if (err.response && err.response.data) {
                    if (err.response.data.error) {
                        errMsg = `${err.response.data.message}: ${err.response.data.error}`;
                    } else if (err.response.data.message) {
                        errMsg = err.response.data.message;
                    }
                }
                this.setState({ error: `Delete failed: ${errMsg}` });
            });
    };

    openPreview = (file) => {
        if (!file || !file.url) {
            this.setState({ error: 'No preview available for this file.' });
            return;
        }
        this.setState({ previewUrl: file.url, previewFileName: file.name, showPreview: true, message: '', error: '' });
    };

    closePreview = () => {
        this.setState({ showPreview: false, previewUrl: null, previewFileName: '' });
    };

    openInNewTab = (url) => {
        if (!url) return;
        window.open(url, '_blank');
    };

    render() {
        const {
            partNumber, selectedDocType, message, error,
            showPreview, previewUrl, previewFileName,
            selectedFile, documents
        } = this.state;

        return (
            <div className="container">
                <h3>Document Control: {getDocTypeLabel(selectedDocType)}</h3>
                <hr />
                {message && <Alert variant="success">{message}</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}

                <Form>
                    <FormGroup as={Row} className="mb-3">
                        <Col sm={2}><Form.Label>Part Number</Form.Label></Col>
                        <Col sm={4}>
                            <FormControl
                                type="text"
                                name="partNumber"
                                placeholder="Enter Part Number"
                                value={partNumber}
                                onChange={this.handleInputChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup as={Row} className="mb-3">
                        <Col sm={2}>
                            <Form.Label>Upload PDF</Form.Label>
                            <div className="text-muted small">(Max 5 files)</div>
                        </Col>
                        <Col sm={4}>
                            <input
                                ref={this.fileInputRef}
                                id="fileInput"
                                type="file"
                                accept=".pdf"
                                className="form-control"
                                onChange={this.onFileChange}
                            />
                        </Col>
                        <Col sm={6} className="d-flex align-items-center">
                            <Button variant="secondary" onClick={this.previewSelected} disabled={!selectedFile} className="me-2">Preview Selected</Button>
                            <Button variant="primary" onClick={this.handleUpload} disabled={!selectedFile}>Upload</Button>
                        </Col>
                    </FormGroup>
                </Form>

                <h4 className="mt-4">Uploaded Files for {getDocTypeLabel(selectedDocType)} (Part: {partNumber || '---'})</h4>
                <div className="mb-2 text-muted small">
                    {partNumber
                        ? `Showing ${documents.length} of 5 allowed files`
                        : `Showing all ${documents.length} documents`}
                </div>

                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>File Name</th>
                            <th>Part Number</th>
                            <th>Upload Date</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {documents && documents.length > 0 ? (
                            documents.map(doc => (
                                <tr key={doc.id}>
                                    <td>{doc.documentName}</td>
                                    <td>{doc.productNumber || '---'}</td>
                                    <td>{new Date(doc.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => {
                                                const baseUrl = config.apiUrl.endsWith('/api')
                                                    ? config.apiUrl.slice(0, -4)
                                                    : config.apiUrl.replace('/api/', '/');
                                                // Ensure no double slashes except after protocol
                                                const fullUrl = (baseUrl + doc.documentPath).replace(/([^:]\/)\/+/g, "$1");
                                                console.log("Previewing document at:", fullUrl);
                                                this.openPreview({
                                                    url: fullUrl,
                                                    name: doc.documentName
                                                });
                                            }}
                                        >Preview</Button>
                                        <Button variant="danger" size="sm" onClick={() => this.handleDelete(doc.id)}>Delete</Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="3" className="text-center">No documents found in database{partNumber ? ` for Part Number "${partNumber}"` : ''}.</td></tr>
                        )}
                    </tbody>
                </Table>

                <Modal show={showPreview} onHide={this.closePreview} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Preview: {previewFileName}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ height: '75vh' }}>
                        {previewUrl ? (
                            <iframe title={previewFileName || 'pdf-preview'} src={previewUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
                        ) : (<div>No preview available.</div>)}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.openInNewTab(previewUrl)} disabled={!previewUrl}>Open in new tab</Button>
                        <Button variant="primary" onClick={this.closePreview}>Close</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

export { DocCon };